/**
 * Work done on this device that the server has not accepted yet.
 *
 * Every change a tech makes lands here first and in the local store second —
 * in one transaction, so the screen can never show an edit that is not also
 * queued. A tech who sees their note on the job card and then loses it because
 * the queue write failed silently has learned not to trust the app, and that
 * is not recoverable.
 *
 * The shape is the server's `ChangeIn`, deliberately: `(entity, entity_id,
 * field, value, op, client_ts)`. Field-level, because row-level last-write-wins
 * destroys work (D3) and a queue that batches whole rows makes the server's
 * careful merge pointless.
 *
 * Three states, and the third is the one that matters:
 *
 *  * **pending** — not sent.
 *  * **sent** — the server took it. Dropped on the next flush.
 *  * **rejected** — the server refused it, or accepted it and could not land
 *    it. These stay, with the reason, until somebody looks at them. Silence
 *    here is how a tech loses an afternoon (D15).
 */

import { device, provisionalNumber } from "./device";
import { type Entity, OUTBOX, type Row, open, patch, readMeta, runRequest, txDone, writeMeta } from "./store";
import { uuid } from "./uuid";

export type Op = "create" | "update" | "delete";

/** One queued change. `seq` is assigned by IndexedDB, in the order work happened. */
export type Queued = {
  seq?: number;
  entity: Entity;
  entity_id: string;
  field: string | null;
  value: unknown;
  op: Op;
  client_ts: string;
  sent_at?: string | null;
  rejected_at?: string | null;
  reason?: string | null;
};

/** What `POST /v1/sync/push` takes. */
export type WireChange = {
  entity: string;
  entity_id: string;
  field: string | null;
  value: unknown;
  op: Op;
  client_ts: string;
};

const LOCAL_SEQ = "local_seq";

/* ------------------------------------------------------------ listeners */

/**
 * Anything that wants to know the queue changed.
 *
 * The status strip has to notice work being queued the moment it happens, not
 * at the next sync — a tech who records an arrival and sees nothing change is
 * being told, wrongly, that it did not take.
 */
type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function announce() {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // A broken listener is not a reason to lose the write that just landed.
    }
  }
}

/* ---------------------------------------------------------------- writing */

/**
 * Create a record here and queue it.
 *
 * The id is minted locally and never reassigned (D2), which is the whole
 * reason offline creation works at all. For entities the customer sees a
 * number on, `numberPrefix` also mints a provisional one — `J-D4-0007` — that
 * goes in `client_ref`. The server swaps in the real number on sync and keeps
 * the provisional, so searching for what the customer was handed still finds
 * the job (D10).
 */
export async function create(
  entity: Entity,
  fields: Record<string, unknown>,
  options: { id?: string; numberPrefix?: string; localOnly?: string[] } = {},
): Promise<Row> {
  const id = options.id ?? uuid();
  // `localOnly` fields are stored so the screen can show them, and stripped
  // from what goes on the wire. The case this exists for is a variation's
  // `status`: the projector's whitelist does not include it, deliberately
  // (D18), so sending it would get the entire create refused — and the tech
  // would lose the variation rather than just its predicted outcome.
  const localOnly = new Set(options.localOnly ?? []);
  const payload: Record<string, unknown> = Object.fromEntries(
    Object.entries(fields).filter(([key]) => !localOnly.has(key)),
  );

  if (options.numberPrefix) {
    const seq = (await readMeta<number>(LOCAL_SEQ, 0)) + 1;
    await writeMeta(LOCAL_SEQ, seq);
    payload.client_ref = provisionalNumber(options.numberPrefix, seq);
  }

  // Stored with everything; queued with only what the server will accept.
  const row: Row = { ...fields, ...payload, id };
  await commit(entity, row, [
    { entity, entity_id: id, field: null, value: payload, op: "create", client_ts: now() },
  ]);
  return row;
}

/**
 * Change fields on a record — one queued change per field.
 *
 * Per field, not per row, because that is the granularity the server merges
 * at. The office retitling a job at 10:02 while the tech, offline since 08:00,
 * adds three parts and a signature has to end with both surviving, and it
 * cannot if this sends a whole row.
 */
export async function update(entity: Entity, id: string, fields: Record<string, unknown>): Promise<Row> {
  const ts = now();
  const changes: Queued[] = Object.entries(fields).map(([field, value]) => ({
    entity,
    entity_id: id,
    field,
    value,
    op: "update" as const,
    client_ts: ts,
  }));
  if (changes.length === 0) return (await patch(entity, id, {})) as Row;

  return commit(entity, { ...fields, id }, changes);
}

export async function destroy(entity: Entity, id: string): Promise<void> {
  const ts = now();
  await commit(
    entity,
    { id, deleted_at: ts },
    [{ entity, entity_id: id, field: null, value: null, op: "delete", client_ts: ts }],
  );
}

/**
 * Write the local row and the queue entries together, or neither.
 *
 * The atomicity is the point. Two transactions means a window where the app
 * shows work that is not queued, or queues work the app does not show.
 */
async function commit(entity: Entity, fields: Row, changes: Queued[]): Promise<Row> {
  const db = await open();
  const tx = db.transaction([entity, OUTBOX], "readwrite");

  const store = tx.objectStore(entity);
  const existing = ((await runRequest(store.get(fields.id))) as Row | undefined) ?? { id: fields.id };
  const merged: Row = { ...existing, ...fields };

  try {
    store.put(merged);
    const outbox = tx.objectStore(OUTBOX);
    for (const change of changes) outbox.put({ ...change, sent_at: null, rejected_at: null, reason: null });
  } catch (cause) {
    tx.abort();
    await txDone(tx).catch(() => {});
    throw cause;
  }

  await txDone(tx);
  announce();
  return merged;
}

/* ---------------------------------------------------------------- reading */

/** Everything not yet sent, oldest first — the order the work happened in. */
export async function pending(): Promise<Queued[]> {
  const db = await open();
  const rows = (await runRequest(
    db.transaction(OUTBOX, "readonly").objectStore(OUTBOX).getAll(),
  )) as Queued[];
  return rows.filter((row) => !row.sent_at && !row.rejected_at);
}

/** Every entry in every state, oldest first. For screens that show progress. */
export async function all(): Promise<Queued[]> {
  const db = await open();
  return (await runRequest(db.transaction(OUTBOX, "readonly").objectStore(OUTBOX).getAll())) as Queued[];
}

/** What the server refused, and why. Shown to a person; never swallowed. */
export async function rejected(): Promise<Queued[]> {
  const db = await open();
  const rows = (await runRequest(
    db.transaction(OUTBOX, "readonly").objectStore(OUTBOX).getAll(),
  )) as Queued[];
  return rows.filter((row) => Boolean(row.rejected_at));
}

export async function counts(): Promise<{ pending: number; rejected: number }> {
  const db = await open();
  const rows = (await runRequest(
    db.transaction(OUTBOX, "readonly").objectStore(OUTBOX).getAll(),
  )) as Queued[];
  return {
    pending: rows.filter((r) => !r.sent_at && !r.rejected_at).length,
    rejected: rows.filter((r) => Boolean(r.rejected_at)).length,
  };
}

/** The wire form of a batch, plus the device id the server merges by. */
export function envelope(changes: Queued[]) {
  return {
    device_id: device().id,
    changes: changes.map(
      ({ entity, entity_id, field, value, op, client_ts }): WireChange => ({
        entity,
        entity_id,
        field,
        value,
        op,
        client_ts,
      }),
    ),
  };
}

/* -------------------------------------------------------------- draining */

/** Mark a batch accepted. They are cleared on the next flush, not now. */
export async function markSent(seqs: number[]): Promise<void> {
  await stamp(seqs, { sent_at: now() });
}

/**
 * Mark a change refused, with the reason in the tech's words.
 *
 * Both kinds of refusal land here: one the merge rejected, and one it accepted
 * that the projector could not land — a line pointing at a job that was never
 * created, a field a device may not set. The server distinguishes them (D15)
 * and so should whatever shows this.
 */
export async function markRejected(seqs: number[], reason: string): Promise<void> {
  await stamp(seqs, { rejected_at: now(), reason });
}

async function stamp(seqs: number[], fields: Partial<Queued>): Promise<void> {
  if (seqs.length === 0) return;
  const db = await open();
  const tx = db.transaction(OUTBOX, "readwrite");
  const store = tx.objectStore(OUTBOX);

  await Promise.all(
    seqs.map(async (seq) => {
      const row = (await runRequest(store.get(seq))) as Queued | undefined;
      if (row) store.put({ ...row, ...fields });
    }),
  );
  await txDone(tx);
  announce();
}

/** Drop what the server has taken. Rejections are kept until acknowledged. */
export async function forgetSent(): Promise<number> {
  const db = await open();
  const tx = db.transaction(OUTBOX, "readwrite");
  const store = tx.objectStore(OUTBOX);
  const rows = (await runRequest(store.getAll())) as Queued[];

  let dropped = 0;
  for (const row of rows) {
    if (row.sent_at && !row.rejected_at && row.seq !== undefined) {
      store.delete(row.seq);
      dropped += 1;
    }
  }
  await txDone(tx);
  return dropped;
}

/** The tech has seen the refusals. Clears them without re-sending. */
export async function acknowledgeRejected(seqs?: number[]): Promise<void> {
  const db = await open();
  const tx = db.transaction(OUTBOX, "readwrite");
  const store = tx.objectStore(OUTBOX);
  const rows = (await runRequest(store.getAll())) as Queued[];

  for (const row of rows) {
    if (!row.rejected_at || row.seq === undefined) continue;
    if (seqs && !seqs.includes(row.seq)) continue;
    store.delete(row.seq);
  }
  return txDone(tx);
}

function now(): string {
  return new Date().toISOString();
}
